import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { FileEntity } from './file.entity';

/**
 * A firmware package that can be served to sensor host devices over the OTA
 * (Over-The-Air) update endpoints. Each row records one uploaded firmware
 * version; the actual binary lives in S3 (referenced via `fkFileId` →
 * `_file`), never on this row.
 *
 * Serving rule: the OTA `/version` endpoint advertises the row with the
 * greatest `effectiveDate` that is already in effect (<= now) and not
 * soft-deleted. Keeping older rows gives us a history of what was served
 * over time even though only the newest in-effect row is ever handed out.
 *
 * The device decides whether to actually upgrade by comparing the version
 * embedded in `fileName` against its own firmware — our endpoint is
 * stateless. The vendor's `UpdateNow` gate is derived, not stored: it is
 * "true" whenever a row is currently in effect (so a firmware is on offer)
 * and "false" only when nothing is in effect. `effectiveDate` is therefore
 * the single control over what (if anything) is served.
 */
@Index('sensor_firmware_effective_date_idx', ['effectiveDate'])
@Index('sensor_firmware_fk__file__id', ['fkFileId'])
@Entity('sensor_firmware')
export class SensorFirmwareEntity extends BaseEntity {
	// Human/firmware-facing version identifier, e.g. "26060100" (the vendor's
	// YYMMDDNN scheme) or a semantic version. Shown in the admin list.
	@Column('varchar', { name: 'version', length: 64 })
	public version!: string;

	// The exact file name the device requests on the download endpoint and the
	// value returned as `FileName` from `/version`, e.g.
	// "update-62s-26060100.pac". Must match what the host is configured to fetch.
	@Column('varchar', { name: 'fileName', length: 255 })
	public fileName!: string;

	// When this firmware becomes the one served. The latest effectiveDate that
	// is <= now wins, which also lets an admin stage a future cutover.
	@Column('datetime', { name: 'effectiveDate' })
	public effectiveDate!: Date;

	// Optional free-text note (e.g. changelog summary) for the admin list.
	@Column('varchar', { name: 'notes', nullable: true, length: 255 })
	public notes!: string | null;

	// FK to the uploaded firmware binary in `_file`. Nullable so a row can be
	// created before/independent of the upload, but in practice always set.
	@Column('int', { name: 'fk__file__id', nullable: true, unsigned: true })
	public fkFileId!: number | null;

	@ManyToOne(() => FileEntity, {
		onDelete: 'NO ACTION',
		onUpdate: 'NO ACTION',
	})
	@JoinColumn([{ name: 'fk__file__id', referencedColumnName: 'id' }])
	public fkFile!: FileEntity;
}
